import { Row } from 'common/components/common/style';
import MetadataSeparator from 'common/components/metadata-strip/MetadataSeparator';
import variables from 'common/config/variables';
import { LiveChannels } from 'common/constants/data-types';
import { FavoriteChannels } from 'common/reducers/favoriteChannels';
import { PlayerControlsState } from 'common/reducers/videoPlayer';
import { getScalablePixel } from 'common/utils';
import { isEmpty, isNil } from 'lodash';
import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { useZapPreview } from '../hooks/zapPreview';
import { ChannelLogo, ChannelPositionText } from './Controls.styles';

interface ChannelPositionProps {
  // Redux injected
  logos: any;
  liveChannels: LiveChannels;
  channelId: string;
  nextChannelId?: string;
  nextChannelPosition?: number;
  playerControlsState: PlayerControlsState;
  favoriteChannels: FavoriteChannels;
  explicitPosition?: number;
  //
  fastZapNumber?: number;
}

function ChannelPosition(props: ChannelPositionProps) {
  const preview = useZapPreview();
  const nextChannelId = props.nextChannelId && (preview?.channelId ?? props.nextChannelId);
  const nextChannelPosition = props.nextChannelId
    ? preview?.position ?? props.nextChannelPosition
    : undefined;

  const fastZapping = useMemo(() => {
    return !isNil(props.fastZapNumber);
  }, [nextChannelId, props.fastZapNumber]);

  const currentChannelId = useMemo(
    () => nextChannelId || props.channelId,
    [props.channelId, nextChannelId]
  );
  const channelLogo = useMemo(() => {
    if (!currentChannelId) {
      return '';
    }

    return props.logos && props.logos[currentChannelId]?.png;
  }, [props.logos, currentChannelId]);

  const channelPosition = useMemo(() => {
    if (isEmpty(props.liveChannels)) {
      return null;
    }
    // Show fast zap number first
    if (!isNil(props.fastZapNumber)) return props.fastZapNumber;

    const favoritePosition = props.favoriteChannels[currentChannelId]?.position;
    const originalPosition = props.liveChannels[currentChannelId]?.position;

    // Show favorite position if channel is added to the list
    const explicitPosition = !isNil(nextChannelPosition)
      ? nextChannelPosition
      : // Show original position when reordering is enabled
      variables.REORDER_CHANNELS_ENABLED
      ? originalPosition
      : props.explicitPosition;

    if (!isNil(explicitPosition)) return explicitPosition;
    if (!isNil(favoritePosition)) return favoritePosition;

    return originalPosition;
  }, [
    props.liveChannels,
    currentChannelId,
    nextChannelPosition,
    props.fastZapNumber,
    props.explicitPosition,
    props.favoriteChannels
  ]);

  if (
    isEmpty(props.logos) ||
    isEmpty(props.liveChannels) ||
    isEmpty(props.channelId) ||
    (!fastZapping && props.playerControlsState === 'channels')
  ) {
    return <span />;
  }

  return (
    <Row>
      <ChannelPositionText data-test-id="player-channel-position">
        {channelPosition}
      </ChannelPositionText>
      {!fastZapping && <MetadataSeparator spacing={8} size={getScalablePixel(32)} />}
      <ChannelLogo
        data-test-id="player-channel-logo"
        visible={!fastZapping}
        src={channelLogo}
        decoding="sync"
      />
    </Row>
  );
}

const mapStateToProps = ({ liveChannels, logos, videoPlayer, favoriteChannels }: any) => ({
  channelId: videoPlayer.channelId,
  explicitPosition: videoPlayer?.video?.channelPosition,
  logos,
  liveChannels,
  nextChannelId: videoPlayer.nextVideo?.channelId,
  nextChannelPosition: videoPlayer.nextVideo?.position,
  playerControlsState: videoPlayer.playerControlsState,
  favoriteChannels
});

export default React.memo(connect(mapStateToProps, {})(ChannelPosition));
